import { motion } from "framer-motion";

interface GanttRow {
  label: string;
  riel: string;
  start: number; // 1-12
  duration: number; // weeks
  color: string;
}

const rows: GanttRow[] = [
  { label: "01 · Escucha", riel: "Riel A", start: 1, duration: 2, color: "#12315c" },
  { label: "02 · Arqueología", riel: "Riel A", start: 3, duration: 2, color: "#1d4a8c" },
  { label: "03 · Estrategia", riel: "Riel A", start: 5, duration: 2, color: "#2f6fb0" },
  { label: "04 · Arquitectura", riel: "Riel A", start: 7, duration: 2, color: "#3B8BD4" },
  { label: "05 · Sistema de Mensajes", riel: "Riel A", start: 9, duration: 2, color: "#2E9BA8" },
  { label: "06 · Consolidación y entrega", riel: "Riel A", start: 11, duration: 2, color: "#17836B" },
  { label: "CEO Visible", riel: "Riel B", start: 1, duration: 12, color: "#5BA3E0" },
  { label: "Piloto de Voz", riel: "Transversal", start: 5, duration: 8, color: "#1D9E75" },
];

const months = [
  { name: "Mes 1", span: 4 },
  { name: "Mes 2", span: 4 },
  { name: "Mes 3", span: 4 },
];

const TOTAL_WEEKS = 12;

const ChiappellaGantt = () => {
  return (
    <section
      id="cronograma"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, hsl(220 69% 2%), hsl(217 61% 10%))",
      }}
    >
      <div className="container px-4 max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <span
            className="text-sm font-semibold tracking-[0.25em] uppercase mb-4 block"
            style={{ color: "#5BA3E0" }}
          >
            Cronograma
          </span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1]">
            90 días,
            <br />
            <span style={{ color: "#5BA3E0" }}>dos rieles en paralelo.</span>
          </h2>
        </motion.div>

        {/* Gantt container — scrollable on mobile */}
        <div className="overflow-x-auto -mx-4 px-4 pb-4">
          <div
            className="min-w-[760px] rounded-2xl border border-white/10 p-5 md:p-8"
            style={{ background: "hsl(220 69% 4%)" }}
          >
            {/* Months header */}
            <div
              className="grid mb-2"
              style={{
                gridTemplateColumns: `240px repeat(${TOTAL_WEEKS}, minmax(0, 1fr))`,
              }}
            >
              <div />
              {months.map((m) => (
                <div
                  key={m.name}
                  className="text-xs md:text-sm font-semibold tracking-wider uppercase text-white/60 text-center border-b border-white/10 pb-2"
                  style={{ gridColumn: `span ${m.span} / span ${m.span}` }}
                >
                  {m.name}
                </div>
              ))}
            </div>

            {/* Weeks header */}
            <div
              className="grid mb-5"
              style={{
                gridTemplateColumns: `240px repeat(${TOTAL_WEEKS}, minmax(0, 1fr))`,
              }}
            >
              <div />
              {Array.from({ length: TOTAL_WEEKS }, (_, i) => (
                <div
                  key={i}
                  className="text-[10px] md:text-xs text-white/40 text-center"
                >
                  S{i + 1}
                </div>
              ))}
            </div>

            {/* Rows */}
            <div className="space-y-3">
              {rows.map((row, index) => (
                <div
                  key={row.label}
                  className="grid items-center"
                  style={{
                    gridTemplateColumns: `240px repeat(${TOTAL_WEEKS}, minmax(0, 1fr))`,
                  }}
                >
                  {/* Label */}
                  <div className="pr-4">
                    <p className="text-sm md:text-[15px] text-white/85 font-medium leading-tight">
                      {row.label}
                    </p>
                    <p
                      className="text-[10px] uppercase tracking-[0.18em] mt-0.5"
                      style={{
                        color:
                          row.riel === "Transversal" ? "#4FD1A5" : "rgba(255,255,255,0.45)",
                      }}
                    >
                      {row.riel}
                    </p>
                  </div>

                  {/* Track */}
                  <div
                    className="relative h-9 rounded-md"
                    style={{
                      gridColumn: `span ${TOTAL_WEEKS} / span ${TOTAL_WEEKS}`,
                      background:
                        "repeating-linear-gradient(to right, rgba(255,255,255,0.03) 0 calc(100%/12), rgba(255,255,255,0.06) calc(100%/12) calc(100%/12 + 1px))",
                    }}
                  >
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.9,
                        delay: index * 0.12,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className="absolute top-1 bottom-1 rounded-md flex items-center px-3"
                      style={{
                        left: `${((row.start - 1) / TOTAL_WEEKS) * 100}%`,
                        width: `${(row.duration / TOTAL_WEEKS) * 100}%`,
                        background: `linear-gradient(90deg, ${row.color}, ${row.color}cc)`,
                        transformOrigin: "left",
                        boxShadow: `0 6px 20px -8px ${row.color}`,
                      }}
                    >
                      <span className="text-[10px] md:text-xs font-semibold text-white/95 whitespace-nowrap">
                        {row.duration} sem
                      </span>
                    </motion.div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footnote */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center text-sm md:text-base text-white/60 mt-8"
        >
          Inicio dentro de los 5 días hábiles de aceptada la propuesta · 12
          semanas de ejecución
        </motion.p>
      </div>
    </section>
  );
};

export default ChiappellaGantt;
